import React, {
    useEffect,
    useRef,
    useState
} from "react";

import { useLabAccess } from "../../contexts/LabAccessContext";
import { useDataProvider } from "../../hooks/data/DataProvider";

const CreateProject = ({
    labId,
    gameId,
    ideType,
    level,
    onBack
}) => {

    const { openProject } = useLabAccess();

    const {
        getGame,
        addData
    } = useDataProvider();

    const [projectName, setProjectName] = useState("");
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState("");

    const mountedRef = useRef(false);


    // ============================================================
    // GET SELECTED GAME
    // ============================================================

    const game = getGame(gameId);


    // ============================================================
    // MOUNT / UNMOUNT
    // ============================================================

    useEffect(() => {

        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
        };

    }, []);


    // ============================================================
    // CREATE PROJECT
    // ============================================================

    const createProject = async () => {

        if (!projectName.trim()) {

            if (mountedRef.current) {
                setError(
                    "Please enter a project name."
                );
            }

            return;
        }

        if (creating) {
            return;
        }

        if (
            labId === null ||
            labId === undefined
        ) {
            setError(
                "Lab is required."
            );
            return;
        }

        if (
            gameId === null ||
            gameId === undefined
        ) {
            setError(
                "Game is required."
            );
            return;
        }

        try {

            setCreating(true);
            setError("");

            // ----------------------------------------------------
            // Build project data
            // ----------------------------------------------------

            const projectData = {
                lab_id: labId,
                game_id: gameId,
                name: projectName.trim(),
                ide_type: ideType,
                level:
                    ideType === "simple"
                        ? level
                        : null
            };

            console.log(
                "CREATE PROJECT:",
                projectData
            );

            // ----------------------------------------------------
            // Create through DataProvider
            // ----------------------------------------------------

            const project = await addData(
                projectData
            );

            console.log(
                "CREATED PROJECT:",
                project
            );

            if (!project || !project.id) {
                throw new Error(
                    "Project was created but no project ID was returned."
                );
            }

            /*
             * openProject may navigate to another page and
             * unmount this component.
             *
             * Do not update component state after this call.
             */

            openProject(
                project.id,
                project.lab_id ?? labId,
                project.ide_type ?? ideType,
                project.level ??
                    (
                        ideType === "simple"
                            ? level
                            : null
                    )
            );

        } catch (err) {

            console.error(
                "CREATE PROJECT ERROR:",
                err
            );

            if (!mountedRef.current) {
                return;
            }

            setError(
                err.message ||
                "Unable to create project."
            );

            setCreating(false);
        }
    };


    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="projects-screen">

            {/* ====================================================
                TOP BAR
            ==================================================== */}

            <div className="projects-topbar">

                <button
                    type="button"
                    className="back-button"
                    disabled={creating}
                    onClick={onBack}
                >
                    ← Back
                </button>

            </div>


            {/* ====================================================
                HEADER
            ==================================================== */}

            <div className="projects-header">

                <h1>
                    Create Your Project
                </h1>

                <p>
                    Give your project a name.
                </p>

            </div>


            {/* ====================================================
                ERROR
            ==================================================== */}

            {error && (
                <div className="projects-error">
                    {error}
                </div>
            )}


            {/* ====================================================
                CREATE PROJECT CARD
            ==================================================== */}

            <div className="create-project-card">


                {/* =================================================
                    SELECTED GAME / IDE SUMMARY
                ================================================= */}

                <div className="selected-event-summary">

                    <span className="event-label">
                        GAME
                    </span>

                    <h2>
                        {game?.name || "Selected Game"}
                    </h2>

                    {game?.description && (
                        <p>
                            {game.description}
                        </p>
                    )}


                    <span className="event-label">
                        PROJECT TYPE
                    </span>

                    <h2>
                        {ideType === "simple"
                            ? "Simple IDE"
                            : "Advanced IDE"}
                    </h2>


                    {ideType === "simple" && (
                        <p>
                            {level === "beginner"
                                ? "Beginner"
                                : "Experienced"}
                        </p>
                    )}

                </div>


                {/* =================================================
                    PROJECT NAME
                ================================================= */}

                <div className="project-form">

                    <label htmlFor="project-name">
                        Project Name
                    </label>

                    <input
                        id="project-name"
                        type="text"
                        value={projectName}
                        onChange={(e) => {
                            setProjectName(
                                e.target.value
                            );

                            if (error) {
                                setError("");
                            }
                        }}
                        placeholder="Enter your project name"
                        maxLength={255}
                        disabled={creating}
                        autoFocus
                        onKeyDown={(e) => {

                            if (
                                e.key === "Enter" &&
                                projectName.trim() &&
                                !creating
                            ) {
                                createProject();
                            }

                        }}
                    />

                </div>


                {/* =================================================
                    ACTION BUTTONS
                ================================================= */}

                <div className="project-action-buttons">

                    <button
                        type="button"
                        className="cancel-button"
                        disabled={creating}
                        onClick={onBack}
                    >
                        Back
                    </button>


                    <button
                        type="button"
                        className="open-lab-button"
                        disabled={
                            creating ||
                            !projectName.trim()
                        }
                        onClick={createProject}
                    >
                        {creating
                            ? "Creating Project..."
                            : "Create & Open Project →"}
                    </button>

                </div>

            </div>

        </div>
    );
};

export default CreateProject;